// AES encryption utility for password encryption using Web Crypto API
// Compatible with Node.js server implementation

// Fixed encryption key (must match server's SECRET_KEY)
const SECRET_KEY = '9a2b7c4d1e5f80316789a1b2c3d4e5f60718293a4b5c6d7e8f90123456789abc'; // 64 hex chars = 32 bytes for AES-256

/**
 * Converts hex string to Uint8Array
 */
function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

/**
 * Encrypts a password using AES-GCM encryption (compatible with server)
 */
export const encryptPassword = async (password: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    
    // Convert the hex secret key to Uint8Array (32 bytes for AES-256)
    const keyBytes = hexToUint8Array(SECRET_KEY);
    
    // Import the key
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes.buffer as ArrayBuffer,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    
    // Generate a random IV (12 bytes for GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt the data using AES-GCM
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      data
    );
    
    // Combine IV, encrypted data, and auth tag
    // The auth tag is automatically appended by Web Crypto API
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    // Convert to base64 for transmission
    return btoa(String.fromCharCode.apply(null, Array.from(combined)));
  } catch (error) {
    console.error('Password encryption failed:', error);
    throw new Error('Failed to encrypt password');
  }
};

/**
 * Decrypts a password from AES-GCM encryption (compatible with server)
 */
export const decryptPassword = async (encryptedPassword: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    
    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedPassword).split('').map(char => char.charCodeAt(0))
    );
    
    // Extract IV, encrypted data, and auth tag (exactly like server)
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12, -16);
    const tag = combined.slice(-16);
    
    // Convert the hex secret key to Uint8Array (32 bytes for AES-256)
    const keyBytes = hexToUint8Array(SECRET_KEY);
    
    // Import the key
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes.buffer as ArrayBuffer,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Combine encrypted data and auth tag for Web Crypto API
    const encryptedWithTag = new Uint8Array(encrypted.length + tag.length);
    encryptedWithTag.set(encrypted, 0);
    encryptedWithTag.set(tag, encrypted.length);
    
    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encryptedWithTag
    );
    
    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Password decryption failed:', error);
    throw new Error('Failed to decrypt password');
  }
};

/**
 * Decodes a reset password token to extract email, user_id, and expiry
 */
export const decodeResetToken = async (token: string): Promise<{ email: string; user_id: string; expiry: number; valid: boolean }> => {
  try {
    // URL decode the token first
    const decodedToken = decodeURIComponent(token);
    
    let decryptedData = '';
    let tokenData = null;
    
    // Try Method 1: AES-GCM decryption with URL-decoded token
    try {
      decryptedData = await decryptPassword(decodedToken);
      
      // Check if it's JSON format
      if (decryptedData.startsWith('{') || decryptedData.startsWith('[')) {
        tokenData = JSON.parse(decryptedData);
      } else {
        // Try to parse the custom format: user_id$@$email$@$timestamp
        const parts = decryptedData.split('$@$');
        if (parts.length === 3) {
          const [user_id, email, timestamp] = parts;
          tokenData = {
            user_id: user_id.trim(),
            email: email.trim(),
            expiry: parseInt(timestamp.trim()) * 1000 // Convert to milliseconds
          };
        } else {
          throw new Error('Invalid token format');
        }
      }
    } catch (method1Error: any) {
      // Try Method 2: AES-GCM decryption with original token
      try {
        decryptedData = await decryptPassword(token);
        
        // Check if it's JSON format
        if (decryptedData.startsWith('{') || decryptedData.startsWith('[')) {
          tokenData = JSON.parse(decryptedData);
        } else {
          // Try to parse the custom format: user_id$@$email$@$timestamp
          const parts = decryptedData.split('$@$');
          if (parts.length === 3) {
            const [user_id, email, timestamp] = parts;
            tokenData = {
              user_id: user_id.trim(),
              email: email.trim(),
              expiry: parseInt(timestamp.trim()) * 1000 // Convert to milliseconds
            };
          } else {
            throw new Error('Invalid token format');
          }
        }
      } catch (method2Error: any) {
        throw new Error('Token decryption failed');
      }
    }
    
    if (!tokenData) {
      throw new Error('Could not parse token data');
    }
    
    // Check if token has expired (1 hour = 3600 seconds)
    const currentTime = Date.now();
    const tokenCreationTime = tokenData.expiry || 0; // This is actually the creation timestamp
    const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
    const isExpired = tokenCreationTime && (currentTime - tokenCreationTime) > oneHourInMs;
    
    const result = {
      email: tokenData.email || '',
      user_id: tokenData.user_id || '',
      expiry: tokenData.expiry || 0,
      valid: !isExpired && tokenData.email && tokenData.user_id
    };
    
    return result;
  } catch (error: any) {
    console.error('Token decoding error:', error);
    const errorResult = {
      email: '',
      user_id: '',
      expiry: 0,
      valid: false
    };
    
    return errorResult;
  }
};
